/**
 * Centralized tag generation utilities
 * Eliminates duplicate tag logic across services and transformers
 */

type RelationField = number | string | { title: string } | null;

/**
 * Extract title from ID, string, or object relation field
 */
function extractTitle(field: RelationField | undefined): string {
  if (!field) return '';
  if (typeof field === 'number') return String(field);
  if (typeof field === 'string') return field;
  if (typeof field === 'object' && 'title' in field) return field.title;
  return '';
}

/**
 * Get blockchain tag from blockchain field
 */
export function getBlockchainTag(blockchain: RelationField | undefined): string | null {
  const title = extractTitle(blockchain);
  return title ? title.toUpperCase() : null;
}

/**
 * Get token standard tag
 */
export function getTokenStandardTag(standard: string | RelationField | undefined): string | null {
  if (!standard) return null;
  if (typeof standard === 'string') return standard.toUpperCase();
  const title = extractTitle(standard);
  return title ? title.toUpperCase() : null;
}

/**
 * Get year tag from multiple possible date fields
 * Tries each date in order and returns the first valid year
 */
export function getYearTag(...dates: (string | Date | undefined | null)[]): string | null {
  for (const date of dates) {
    if (date) {
      try {
        const year = new Date(String(date)).getFullYear();
        if (year && !isNaN(year) && year > 1900 && year < 2100) {
          return year.toString();
        }
      } catch {
        // Invalid date, try next
      }
    }
  }
  return null;
}

/**
 * Get marketplace tag from marketplace field
 */
export function getMarketplaceTag(marketplace: RelationField | undefined): string | null {
  const title = extractTitle(marketplace);
  return title ? title.toUpperCase() : null;
}

/**
 * Get category tags from category array or single category
 */
export function getCategoryTags(categories: RelationField[] | RelationField | undefined): string[] {
  if (!categories) return [];

  const categoryArray = Array.isArray(categories) ? categories : [categories];
  const tags: string[] = [];

  categoryArray.forEach(category => {
    const title = extractTitle(category);
    if (title) tags.push(title);
  });

  return tags;
}

/**
 * Get tag from generic relation field
 */
export function getRelationTag(field: RelationField | undefined, uppercase = false): string | null {
  const title = extractTitle(field);
  if (!title) return null;
  return uppercase ? title.toUpperCase() : title;
}

/**
 * Process tag array (handles both numeric IDs and objects with title)
 */
export function processTagArray(tags: (number | { title: string })[] | undefined | null): string[] {
  if (!tags || !Array.isArray(tags)) return [];

  const result: string[] = [];
  tags.forEach(tag => {
    if (typeof tag === 'number') {
      result.push(String(tag));
    } else if (tag && typeof tag === 'object' && 'title' in tag) {
      result.push(tag.title);
    }
  });

  return result;
}

/**
 * Truncate text to specified number of words (for medium/platform tags)
 */
export function truncateToWords(text: string | undefined, wordCount: number = 3): string | null {
  if (!text) return null;
  const words = text.split(' ').slice(0, wordCount).join(' ');
  return words || null;
}

/**
 * Combine multiple tags, filtering out nulls and duplicates
 */
export function combineTags(...tags: (string | string[] | null | undefined)[]): string[] {
  const allTags: string[] = [];

  tags.forEach(tag => {
    if (Array.isArray(tag)) {
      allTags.push(...tag);
    } else if (tag) {
      allTags.push(tag);
    }
  });

  // Remove duplicates and filter empty strings
  return [...new Set(allTags.filter(tag => tag && tag.trim()))];
}

/**
 * Helper to build tags for artwork-like entities
 */
export function buildArtworkTags(options: {
  blockchain?: RelationField;
  standard?: string | RelationField;
  oneofone?: boolean;
  dates?: (string | Date | undefined | null)[];
  marketplace?: RelationField;
  categories?: RelationField[] | RelationField;
  tags?: (number | { title: string })[];
  medium?: string;
  platform?: string;
}): string[] {
  return combineTags(
    getBlockchainTag(options.blockchain),
    getTokenStandardTag(options.standard),
    options.oneofone ? 'ONEOFONE' : null,
    getYearTag(...(options.dates || [])),
    getMarketplaceTag(options.marketplace),
    getCategoryTags(options.categories),
    processTagArray(options.tags),
    truncateToWords(options.medium),
    truncateToWords(options.platform)
  );
}